import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography, CircularProgress, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import api from '../api';
import { useLanguage } from '../i18n/LanguageContext';

interface FoodProduct {
  name: string;
  brand: string;
  kcal_per_100g: number;
  protein_per_100g: number;
  carbs_per_100g: number;
  fat_per_100g: number;
  image_url: string;
  serving_size: string;
  barcode: string;
}

interface BarcodeScannerProps {
  onProductScanned: (product: FoodProduct) => void;
  onClose: () => void;
}

const cornerBase = {
  position: 'absolute' as const,
  width: 28,
  height: 28,
};

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({ onProductScanned, onClose }) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scannedRef = useRef(false);
  const scanningRef = useRef(true);
  const { t } = useLanguage();
  const theme = useTheme();

  useEffect(() => {
    let cancelled = false;

    const startScanning = async () => {
      // Check BarcodeDetector support
      if (!('BarcodeDetector' in window)) {
        if (!cancelled) setError(t.barcodeScannerNotSupported);
        return;
      }

      // Get camera stream
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
      } catch (err) {
        if (!cancelled) {
          const msg = String((err as Error)?.message || err || '');
          const isPermission = /permission|not allowed|denied|blocked/i.test(msg);
          setError(isPermission ? t.cameraPermissionDenied : msg || t.cameraPermissionDenied);
        }
        return;
      }

      if (cancelled) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }

      streamRef.current = stream;

      // Attach stream to video element
      const video = videoRef.current;
      if (!video) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }
      video.srcObject = stream;
      try {
        await video.play();
      } catch {
        // autoplay attribute handles playback
      }

      // Create detector
      const BarcodeDetectorClass = (window as Record<string, unknown>).BarcodeDetector as new (opts: { formats: string[] }) => { detect: (source: HTMLVideoElement) => Promise<Array<{ rawValue: string }>> };
      const detector = new BarcodeDetectorClass({
        formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'qr_code'],
      });

      // Scan loop
      const scan = async () => {
        if (cancelled || !scanningRef.current || scannedRef.current) return;
        if (video.readyState < video.HAVE_ENOUGH_DATA) {
          requestAnimationFrame(scan);
          return;
        }

        try {
          const barcodes = await detector.detect(video);
          if (barcodes.length > 0 && !scannedRef.current && !cancelled) {
            scannedRef.current = true;
            const barcode = barcodes[0].rawValue;
            setLoading(true);
            setError(null);

            try {
              const response = await api.get(`/food/barcode/${barcode}`);
              if (!cancelled) onProductScanned(response.data);
            } catch {
              if (!cancelled) setError(t.barcodeNotFound);
              scannedRef.current = false;
            } finally {
              if (!cancelled) setLoading(false);
            }
          }
        } catch {
          // Detection failed for this frame — continue scanning
        }

        if (!cancelled && scanningRef.current) {
          requestAnimationFrame(scan);
        }
      };

      requestAnimationFrame(scan);
    };

    startScanning().catch(() => {});

    return () => {
      cancelled = true;
      scanningRef.current = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, [onProductScanned, t]);

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Camera wrapper */}
      <Box sx={{ position: 'relative', minHeight: 280 }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{ width: '100%', display: 'block', borderRadius: 8 }}
        />

        {/* Overlay — transparent, sits above video */}
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          pointerEvents: 'none', zIndex: 10,
        }}>
          <Box sx={{ ...cornerBase, top: '8%', left: '8%', borderTop: `3px solid ${theme.palette.primary.main}`, borderLeft: `3px solid ${theme.palette.primary.main}` }} />
          <Box sx={{ ...cornerBase, top: '8%', right: '8%', borderTop: `3px solid ${theme.palette.primary.main}`, borderRight: `3px solid ${theme.palette.primary.main}` }} />
          <Box sx={{ ...cornerBase, bottom: '8%', left: '8%', borderBottom: `3px solid ${theme.palette.primary.main}`, borderLeft: `3px solid ${theme.palette.primary.main}` }} />
          <Box sx={{ ...cornerBase, bottom: '8%', right: '8%', borderBottom: `3px solid ${theme.palette.primary.main}`, borderRight: `3px solid ${theme.palette.primary.main}` }} />

          {!loading && (
            <Box sx={{
              '@keyframes scanLine': {
                '0%':   { top: '12%' },
                '50%':  { top: '78%' },
                '100%': { top: '12%' },
              },
              position: 'absolute',
              left: '8%', right: '8%', height: 2,
              background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
              boxShadow: `0 0 6px ${theme.palette.primary.main}`,
              animation: 'scanLine 2s ease-in-out infinite',
            }} />
          )}

          {loading && (
            <Box sx={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'rgba(0,0,0,0.45)',
            }}>
              <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1.5 }}>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="body2" color="text.secondary">{t.lookingUpProduct}</Typography>
          </Box>
        ) : error ? (
          <Typography color="error" variant="body2" sx={{ textAlign: 'center' }}>{error}</Typography>
        ) : (
          <Chip
            label={t.scanningBarcode}
            size="small"
            sx={{
              '@keyframes barcodePulse': {
                '0%': { opacity: 1 }, '50%': { opacity: 0.4 }, '100%': { opacity: 1 },
              },
              bgcolor: 'transparent', color: theme.palette.primary.main, border: `1px solid ${theme.palette.primary.main}`,
              animation: 'barcodePulse 1.8s ease-in-out infinite', fontWeight: 500,
            }}
          />
        )}
      </Box>

      <Button variant="outlined" fullWidth onClick={onClose}>{t.cancel}</Button>
    </Box>
  );
};

export default BarcodeScanner;
